import { type ChangeEvent, type FC, useState } from "react";
import styled from "styled-components";
import { PayableEntityType } from "@shared_packages/toolkit/types";
import {
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
} from "../../../../shared-packages/packages/toolkit/types/src/company-subscription-fees-types";
import { SubscriptionPriceProfileKey } from "../../../../shared-packages/packages/toolkit/types/src/subscription-price-profile-types";
import {
  TextInput,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import type { SubscriptionFeeSchedule } from "../../../../util/types";
import {
  FEE_CURRENCY_SYMBOLS,
  FEE_TYPE_ROWS,
  BULK_FIXED_MONTHLY_PLAN_OPTIONS,
  PO_BILLING_FEE_CURRENCY_CODES,
  addCurrencySchedule,
  emptyCurrencySchedule,
  ensureGbpSchedule,
  feeRuleByType,
  preventNumberInputWheel,
  removeCurrencySchedule,
  updateCurrencySchedule,
  upsertFeeRuleByType,
} from "./subscription-billing.helpers";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ToggleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`;

const ToggleLabel = styled.label`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  color: #13273f;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{
  $on: boolean;
  $locked?: boolean;
  $disabled?: boolean;
}>`
  border: 1px solid ${({ $on }) => ($on ? "#027aff" : "#d1d5db")};
  background: ${({ $on }) => ($on ? "#eef6ff" : "#fff")};
  color: ${({ $disabled, $on }) =>
    $disabled ? "#9ca3af" : $on ? "#027aff" : "#374151"};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: ${({ $disabled, $locked }) =>
    $disabled || $locked ? "default" : "pointer"};
  opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
`;

const ScheduleCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #374151;
`;

const Caption = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const FxBanner = styled.div<{ $warning?: boolean }>`
  border: 1px solid ${({ $warning }) => ($warning ? "#f2cf7d" : "#b8d8ff")};
  background: ${({ $warning }) => ($warning ? "#fff9e8" : "#f4f9ff")};
  border-radius: 6px;
  color: #374151;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
`;

const CurrencyHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const FxRateText = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

const MinimumRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(160px, auto);
  gap: 12px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ConvertedMinimum = styled.div`
  color: #374151;
  font-size: 12px;
  white-space: nowrap;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TableRow = styled.div<{ $advanced?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $advanced }) =>
    $advanced ? "130px minmax(180px, 1fr) 2fr" : "130px 1fr"};
  gap: 10px;
  align-items: start;
  padding: 10px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  color: #13273f;
`;

const TypeLabel = styled.div`
  font-weight: 600;
  padding-top: 24px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
`;

const Select = styled.select`
  height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 10px;
  background: #fff;
  color: #13273f;
`;

export interface SubscriptionPricingFxRates {
  billingCurrency: string;
  asOfDate: string;
  fetchedAt: string;
  ratesToBillingCurrency: Record<string, number>;
}

interface Props {
  value: SubscriptionFeeSchedule[] | null | undefined;
  onChange: (next: SubscriptionFeeSchedule[]) => void;
  pricingMode?: PaymentsPricingMode;
  scope?: "uk" | "eu" | "all";
  canAccessInternationalOb?: boolean;
  canAccessRecurringPaymentsUk?: boolean;
  canAccessVrpUk?: boolean;
  canAccessBulkPaymentsUk?: boolean;
  enabledPaymentCurrencies?: string[];
  /** EU subscription mode: currencies are explicit, GBP is not injected, and company access toggles are hidden. */
  standaloneEu?: boolean;
  onEnabledPaymentCurrenciesChange?: (currencies: string[]) => void;
  billingCurrency?: string | null;
  fxRates?: SubscriptionPricingFxRates | null;
  fxRatesLoading?: boolean;
  fxRatesError?: boolean;
  onCapabilitiesChange?: (next: {
    canAccessInternationalOb?: boolean;
    canAccessRecurringPaymentsUk?: boolean;
    canAccessVrpUk?: boolean;
    canAccessBulkPaymentsUk?: boolean;
  }) => void;
}

function parseDecimal(raw: string): number | null {
  if (raw === "" || raw === "." || raw === ",") return null;
  const n = parseFloat(raw.replace(",", "."));
  return isNaN(n) ? null : n;
}

function parseMajorToPence(raw: string): number | null {
  const n = parseDecimal(raw);
  if (n == null) return null;
  return Math.round(n * 100);
}

function parsePct(raw: string): number | null {
  const n = parseDecimal(raw);
  return n == null ? null : n;
}

function sanitizeDecimalInput(raw: string): string {
  const normalized = raw.replace(",", ".");
  if (normalized === "") return "";
  const match = normalized.match(/^\d*(?:\.\d{0,2})?/);
  return match?.[0] ?? "";
}

function sanitizeIntegerInput(raw: string): string {
  const match = raw.match(/\d*/);
  return match?.[0] ?? "";
}

function parseInteger(raw: string): number | null {
  if (raw === "") return null;
  const n = Number(raw);
  return isNaN(n) ? null : Math.round(n);
}

function displayMajor(pence: number | null | undefined): string {
  return pence != null ? String(pence / 100) : "";
}

function mustUseCustomFees(payableEntityType: PayableEntityType): boolean {
  return payableEntityType === PayableEntityType.VRP;
}

function supportsPerSetupFee(payableEntityType: PayableEntityType): boolean {
  return payableEntityType === PayableEntityType.RECURRING_PAYMENT;
}

function availableTypeRows(
  currency: string,
  canAccessRecurringPaymentsUk: boolean,
  canAccessVrpUk: boolean,
  canAccessBulkPaymentsUk: boolean
) {
  if (currency !== "gbp") {
    return FEE_TYPE_ROWS.filter(
      (row) => row.type === PayableEntityType.PAYMENT
    );
  }
  return FEE_TYPE_ROWS.filter((row) => {
    if (row.type === PayableEntityType.RECURRING_PAYMENT) {
      return canAccessRecurringPaymentsUk;
    }
    if (row.type === PayableEntityType.VRP) return canAccessVrpUk;
    if (row.type === PayableEntityType.BULK_PAYMENT) {
      return canAccessBulkPaymentsUk;
    }
    return true;
  });
}

const TransactionFeesScheduleEditor: FC<Props> = ({
  value,
  onChange,
  pricingMode = PaymentsPricingMode.RULE_BASED,
  scope = "all",
  canAccessInternationalOb = false,
  canAccessRecurringPaymentsUk = false,
  canAccessVrpUk = false,
  canAccessBulkPaymentsUk = false,
  enabledPaymentCurrencies,
  standaloneEu = false,
  onEnabledPaymentCurrenciesChange,
  billingCurrency = "gbp",
  fxRates,
  fxRatesLoading = false,
  fxRatesError = false,
  onCapabilitiesChange,
}) => {
  const fees = standaloneEu
    ? ensureGbpSchedule(value).filter((schedule) => schedule.currency !== "gbp")
    : ensureGbpSchedule(value);
  const enabled = new Set(
    (enabledPaymentCurrencies?.length
      ? enabledPaymentCurrencies
      : fees.map((schedule) => schedule.currency)
    ).map((currency) => currency.toLowerCase())
  );
  if (!standaloneEu) enabled.add("gbp");
  const ordered = PO_BILLING_FEE_CURRENCY_CODES.map((code) =>
    code.toLowerCase()
  ).filter((code) => {
    if (!enabled.has(code)) return false;
    if (scope === "uk") return code === "gbp";
    if (scope === "eu") return code !== "gbp";
    return true;
  });
  const ruleBasedMode = pricingMode === PaymentsPricingMode.RULE_BASED;
  const normalizedBillingCurrency = String(
    billingCurrency || "gbp"
  ).toLowerCase();
  const billingSymbol =
    FEE_CURRENCY_SYMBOLS[normalizedBillingCurrency] ??
    normalizedBillingCurrency.toUpperCase();
  const [draftInputs, setDraftInputs] = useState<Record<string, string>>({});

  const formatRateDate = (value: string, includeTime = false) => {
    const date = new Date(includeTime ? value : `${value}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    }).format(date);
  };

  const formatFxRate = (rate: number) =>
    new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(rate);

  const formatCurrencyMinor = (currency: string, amountMinor: number) =>
    `${currency.toUpperCase()} ${(amountMinor / 100).toFixed(2)}`;

  const setFees = (next: SubscriptionFeeSchedule[]) => {
    const normalized = standaloneEu
      ? ensureGbpSchedule(next).filter(
          (schedule) => schedule.currency !== "gbp"
        )
      : ensureGbpSchedule(next);
    const sanitized = normalized.map((schedule) => {
      const minimumSupported = schedule.rules.every(
        (rule) =>
          rule.pricingStrategy === SubscriptionFeePricingStrategy.CUSTOM_FEES ||
          rule.pricingStrategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE
      );
      return minimumSupported
        ? schedule
        : { ...schedule, minimumMonthlyFeeMinor: null };
    });
    const incompatibleCurrencies = new Set(
      sanitized
        .filter((schedule) => schedule.minimumMonthlyFeeMinor == null)
        .filter((schedule) =>
          schedule.rules.some(
            (rule) =>
              rule.pricingStrategy !==
                SubscriptionFeePricingStrategy.CUSTOM_FEES &&
              rule.pricingStrategy !==
                SubscriptionFeePricingStrategy.PER_SETUP_FEE
          )
        )
        .map((schedule) => schedule.currency)
    );
    if (incompatibleCurrencies.size > 0) {
      setDraftInputs((current) => {
        const nextDrafts = { ...current };
        incompatibleCurrencies.forEach((currency) => {
          delete nextDrafts[`${currency}:minimumMonthlyFeeMinor`];
        });
        return nextDrafts;
      });
    }
    onChange(sanitized);
  };

  const draftValue = (key: string, fallback: string) =>
    Object.prototype.hasOwnProperty.call(draftInputs, key)
      ? draftInputs[key]
      : fallback;
  const startDraft = (key: string, fallback: string) => {
    setDraftInputs((current) => ({ ...current, [key]: fallback }));
  };
  const changeDraft = (key: string, next: string) => {
    setDraftInputs((current) => ({ ...current, [key]: next }));
  };
  const finishDraft = (key: string) => {
    setDraftInputs((current) => {
      const { [key]: _finished, ...rest } = current;
      return rest;
    });
  };

  return (
    <Wrap>
      {onCapabilitiesChange && !standaloneEu && (
        <ToggleGrid>
          {scope !== "eu" && (
            <>
              <ToggleLabel>
                <input
                  type="checkbox"
                  checked={canAccessRecurringPaymentsUk}
                  onChange={(e) =>
                    onCapabilitiesChange({
                      canAccessRecurringPaymentsUk: e.target.checked,
                    })
                  }
                />
                Recurring payments
              </ToggleLabel>
              <ToggleLabel>
                <input
                  type="checkbox"
                  checked={canAccessVrpUk}
                  onChange={(e) =>
                    onCapabilitiesChange({
                      canAccessVrpUk: e.target.checked,
                    })
                  }
                />
                VRP payments
              </ToggleLabel>
              <ToggleLabel>
                <input
                  type="checkbox"
                  checked={canAccessBulkPaymentsUk}
                  onChange={(e) =>
                    onCapabilitiesChange({
                      canAccessBulkPaymentsUk: e.target.checked,
                    })
                  }
                />
                Bulk payments
              </ToggleLabel>
            </>
          )}
          {scope !== "uk" && (
            <ToggleLabel>
              <input
                type="checkbox"
                checked={canAccessInternationalOb}
                onChange={(e) =>
                  onCapabilitiesChange({
                    canAccessInternationalOb: e.target.checked,
                  })
                }
              />
              International OB
            </ToggleLabel>
          )}
        </ToggleGrid>
      )}

      {scope === "eu" && ruleBasedMode && canAccessInternationalOb && (
        <FxBanner $warning={fxRatesError}>
          {fxRatesLoading
            ? "Loading the latest stored FX rates..."
            : fxRatesError || !fxRates
            ? "FX rates are currently unavailable. Pricing can still be configured and saved."
            : `Usage is measured in each payment currency. At invoice generation, fees are converted to ${fxRates.billingCurrency.toUpperCase()}, the subscription currency. Rates as of ${formatRateDate(
                fxRates.asOfDate
              )}. Last fetched ${formatRateDate(fxRates.fetchedAt, true)}.`}
        </FxBanner>
      )}

      {scope !== "uk" && (
        <>
          <Field>
            Currencies
            <ChipRow>
              {PO_BILLING_FEE_CURRENCY_CODES.filter(
                (code) => scope === "all" || code.toLowerCase() !== "gbp"
              ).map((code) => {
                const key = code.toLowerCase();
                const isGbp = key === "gbp";
                const disabled =
                  !standaloneEu && !isGbp && !canAccessInternationalOb;
                const on = enabled.has(key);
                const chip = (
                  <Chip
                    key={code}
                    type="button"
                    $on={on}
                    $locked={isGbp}
                    $disabled={disabled}
                    aria-disabled={disabled}
                    onClick={() => {
                      if (isGbp || disabled) return;
                      const nextCurrencies = on
                        ? Array.from(enabled).filter(
                            (currency) => currency !== key
                          )
                        : [...Array.from(enabled), key];
                      onEnabledPaymentCurrenciesChange?.(nextCurrencies);
                      if (!ruleBasedMode) return;
                      setFees(
                        on
                          ? removeCurrencySchedule(fees, key)
                          : addCurrencySchedule(fees, key)
                      );
                    }}
                  >
                    {code}
                  </Chip>
                );
                if (!disabled) return chip;
                return (
                  <TooltipText
                    key={code}
                    id={`po-fee-currency-${key}-disabled`}
                    text="Enable International Open Banking for this company before configuring non-GBP pricing."
                  >
                    {chip}
                  </TooltipText>
                );
              })}
            </ChipRow>
          </Field>
          <Caption>
            {scope === "eu"
              ? "Non-GBP pricing supports Single payment only."
              : "GBP is always on. Non-GBP pricing supports Single payment only."}
          </Caption>
        </>
      )}

      {scope === "eu" && ordered.length === 0 && (
        <Caption>No EU currencies selected.</Caption>
      )}

      {ruleBasedMode &&
        ordered.map((currency) => {
          const schedule =
            fees.find((entry) => entry.currency === currency) ??
            emptyCurrencySchedule(currency);
          const isGbp = currency === "gbp";
          const canConfigureCurrency =
            standaloneEu || isGbp || !!canAccessInternationalOb;
          const typeRows = availableTypeRows(
            currency,
            canAccessRecurringPaymentsUk,
            canAccessVrpUk,
            canAccessBulkPaymentsUk
          );
          const symbol =
            FEE_CURRENCY_SYMBOLS[currency] ?? currency.toUpperCase();
          const fxRate =
            fxRates?.ratesToBillingCurrency[currency.toUpperCase()];
          const validFxRate =
            typeof fxRate === "number" && Number.isFinite(fxRate) && fxRate > 0
              ? fxRate
              : null;
          const minimumMonthlyDraftKey = `${currency}:minimumMonthlyFeeMinor`;
          const minimumMonthlySupported = schedule.rules.every(
            (rule) =>
              rule.pricingStrategy ===
                SubscriptionFeePricingStrategy.CUSTOM_FEES ||
              rule.pricingStrategy ===
                SubscriptionFeePricingStrategy.PER_SETUP_FEE
          );

          return (
            <ScheduleCard key={currency}>
              <CurrencyHeading>
                <Typography variant={TypographyVariant.BODY2}>
                  {currency.toUpperCase()}
                </Typography>
                {validFxRate != null && fxRates && (
                  <FxRateText>
                    1 {currency.toUpperCase()} = {formatFxRate(validFxRate)}{" "}
                    {fxRates.billingCurrency.toUpperCase()}
                  </FxRateText>
                )}
              </CurrencyHeading>

              <>
                <MinimumRow>
                  <Field>
                    Minimum monthly (optional)
                    <TextInput
                      type="text"
                      inputMode="decimal"
                      disabled={
                        !canConfigureCurrency || !minimumMonthlySupported
                      }
                      value={draftValue(
                        minimumMonthlyDraftKey,
                        displayMajor(schedule.minimumMonthlyFeeMinor)
                      )}
                      placeholder={`${symbol}0.00`}
                      onWheel={preventNumberInputWheel}
                      onFocus={() =>
                        startDraft(
                          minimumMonthlyDraftKey,
                          displayMajor(schedule.minimumMonthlyFeeMinor)
                        )
                      }
                      onBlur={() => finishDraft(minimumMonthlyDraftKey)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        if (!canConfigureCurrency || !minimumMonthlySupported)
                          return;
                        const nextValue = sanitizeDecimalInput(e.target.value);
                        changeDraft(minimumMonthlyDraftKey, nextValue);
                        setFees(
                          updateCurrencySchedule(fees, currency, {
                            minimumMonthlyFeeMinor:
                              parseMajorToPence(nextValue),
                          })
                        );
                      }}
                    />
                    <Caption>
                      Available only when all payment types use Custom Fees or
                      Per Setup Fee.
                    </Caption>
                  </Field>
                  {validFxRate != null && fxRates && (
                    <ConvertedMinimum>
                      Converted minimum:{" "}
                      <strong>
                        {schedule.minimumMonthlyFeeMinor != null &&
                        schedule.minimumMonthlyFeeMinor > 0
                          ? formatCurrencyMinor(
                              fxRates.billingCurrency,
                              Math.round(
                                schedule.minimumMonthlyFeeMinor * validFxRate
                              )
                            )
                          : "—"}
                      </strong>
                    </ConvertedMinimum>
                  )}
                </MinimumRow>
                <Table>
                  {typeRows.map((row) => {
                    const rule = feeRuleByType(schedule, row.type);
                    const strategy =
                      rule?.pricingStrategy ??
                      SubscriptionFeePricingStrategy.CUSTOM_FEES;
                    const customStrategy =
                      strategy === SubscriptionFeePricingStrategy.CUSTOM_FEES;
                    const perSetupStrategy =
                      strategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE;
                    const fixedMonthlyPlanStrategy =
                      strategy ===
                      SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN;
                    const isBulk = row.type === PayableEntityType.BULK_PAYMENT;
                    const isRecurring =
                      row.type === PayableEntityType.RECURRING_PAYMENT;
                    const customFeesOnly =
                      mustUseCustomFees(row.type) || currency !== "gbp";
                    const showPricingSelect =
                      (ruleBasedMode && !customFeesOnly) ||
                      supportsPerSetupFee(row.type);
                    const planOptions = BULK_FIXED_MONTHLY_PLAN_OPTIONS.filter(
                      (option) => option.currency === normalizedBillingCurrency
                    );
                    const bulkPricingSelection = customStrategy
                      ? "__customFees"
                      : rule?.monthlyPlanKey ??
                        (rule ? "__customPlan" : planOptions[0]?.key ?? "");
                    const flatFeeDraftKey = `${currency}:${row.type}:flatFeeMinor`;
                    const percentageDraftKey = `${currency}:${row.type}:percentageFee`;
                    const fixedMonthlyFeeDraftKey = `${currency}:${row.type}:monthlyFeeMinor`;
                    const fixedMonthlyLimitDraftKey = `${currency}:${row.type}:monthlyUnitLimit`;

                    return (
                      <TableRow key={row.type} $advanced={showPricingSelect}>
                        <TypeLabel>{row.label}</TypeLabel>
                        {showPricingSelect && (
                          <Field>
                            Pricing
                            <Select
                              disabled={!canConfigureCurrency}
                              value={isBulk ? bulkPricingSelection : strategy}
                              onChange={(e) => {
                                if (!canConfigureCurrency) return;
                                const value = e.target.value;
                                if (isBulk) {
                                  if (value === "__customFees") {
                                    setFees(
                                      upsertFeeRuleByType(
                                        fees,
                                        currency,
                                        row.type,
                                        {
                                          pricingStrategy:
                                            SubscriptionFeePricingStrategy.CUSTOM_FEES,
                                          flatFeeMinor:
                                            rule?.flatFeeMinor ?? null,
                                          percentageFee:
                                            rule?.percentageFee ?? null,
                                          monthlyPlanKey: null,
                                          monthlyFeeMinor: null,
                                          monthlyUnitLimit: null,
                                        }
                                      )
                                    );
                                    return;
                                  }
                                  setFees(
                                    upsertFeeRuleByType(
                                      fees,
                                      currency,
                                      row.type,
                                      {
                                        pricingStrategy:
                                          SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN,
                                        flatFeeMinor: null,
                                        percentageFee: null,
                                        monthlyPlanKey:
                                          value === "__customPlan"
                                            ? null
                                            : (value as SubscriptionPriceProfileKey),
                                        monthlyFeeMinor:
                                          value === "__customPlan"
                                            ? rule?.monthlyFeeMinor ?? null
                                            : null,
                                        monthlyUnitLimit:
                                          value === "__customPlan"
                                            ? rule?.monthlyUnitLimit ?? null
                                            : null,
                                      }
                                    )
                                  );
                                  return;
                                }

                                const nextStrategy =
                                  value as SubscriptionFeePricingStrategy;
                                setFees(
                                  upsertFeeRuleByType(
                                    fees,
                                    currency,
                                    row.type,
                                    {
                                      pricingStrategy: nextStrategy,
                                      flatFeeMinor:
                                        nextStrategy ===
                                          SubscriptionFeePricingStrategy.CUSTOM_FEES ||
                                        nextStrategy ===
                                          SubscriptionFeePricingStrategy.PER_SETUP_FEE
                                          ? rule?.flatFeeMinor ?? null
                                          : null,
                                      percentageFee:
                                        nextStrategy ===
                                        SubscriptionFeePricingStrategy.CUSTOM_FEES
                                          ? rule?.percentageFee ?? null
                                          : null,
                                      monthlyPlanKey: null,
                                      monthlyFeeMinor: null,
                                      monthlyUnitLimit: null,
                                    }
                                  )
                                );
                              }}
                            >
                              {!isBulk && !isRecurring && (
                                <>
                                  <option
                                    value={
                                      SubscriptionFeePricingStrategy.STANDARD_TIERED
                                    }
                                  >
                                    Standard tiered
                                  </option>
                                  <option
                                    value={
                                      SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
                                    }
                                  >
                                    High-risk tiered
                                  </option>
                                </>
                              )}
                              {isRecurring && (
                                <option
                                  value={
                                    SubscriptionFeePricingStrategy.PER_SETUP_FEE
                                  }
                                >
                                  Per setup fee
                                </option>
                              )}
                              {isBulk && (
                                <>
                                  <option value="__customFees">
                                    Custom fees
                                  </option>
                                  {planOptions.map((option) => (
                                    <option key={option.key} value={option.key}>
                                      {option.label}
                                    </option>
                                  ))}
                                  <option value="__customPlan">
                                    Custom Sales plan
                                  </option>
                                </>
                              )}
                              {!isBulk && (
                                <option
                                  value={
                                    SubscriptionFeePricingStrategy.CUSTOM_FEES
                                  }
                                >
                                  Custom fees
                                </option>
                              )}
                            </Select>
                          </Field>
                        )}
                        <FieldsGrid>
                          {(customStrategy || perSetupStrategy) && (
                            <>
                              <Field>
                                {perSetupStrategy
                                  ? `Setup fee (${symbol})`
                                  : `Flat fee per transaction (${symbol})`}
                                <TextInput
                                  type="text"
                                  inputMode="decimal"
                                  disabled={!canConfigureCurrency}
                                  placeholder={`${symbol}0.00`}
                                  value={draftValue(
                                    flatFeeDraftKey,
                                    displayMajor(rule?.flatFeeMinor)
                                  )}
                                  onWheel={preventNumberInputWheel}
                                  onFocus={() =>
                                    startDraft(
                                      flatFeeDraftKey,
                                      displayMajor(rule?.flatFeeMinor)
                                    )
                                  }
                                  onBlur={() => finishDraft(flatFeeDraftKey)}
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>
                                  ) => {
                                    if (!canConfigureCurrency) return;
                                    const nextValue = sanitizeDecimalInput(
                                      e.target.value
                                    );
                                    changeDraft(flatFeeDraftKey, nextValue);
                                    setFees(
                                      upsertFeeRuleByType(
                                        fees,
                                        currency,
                                        row.type,
                                        {
                                          pricingStrategy: perSetupStrategy
                                            ? SubscriptionFeePricingStrategy.PER_SETUP_FEE
                                            : SubscriptionFeePricingStrategy.CUSTOM_FEES,
                                          flatFeeMinor:
                                            parseMajorToPence(nextValue),
                                          percentageFee: perSetupStrategy
                                            ? null
                                            : rule?.percentageFee ?? null,
                                        }
                                      )
                                    );
                                  }}
                                />
                              </Field>
                              {customStrategy && (
                                <Field>
                                  Percentage of volume
                                  <TextInput
                                    type="text"
                                    inputMode="decimal"
                                    disabled={!canConfigureCurrency}
                                    placeholder="0%"
                                    value={draftValue(
                                      percentageDraftKey,
                                      rule?.percentageFee != null
                                        ? String(rule.percentageFee)
                                        : ""
                                    )}
                                    onWheel={preventNumberInputWheel}
                                    onFocus={() =>
                                      startDraft(
                                        percentageDraftKey,
                                        rule?.percentageFee != null
                                          ? String(rule.percentageFee)
                                          : ""
                                      )
                                    }
                                    onBlur={() =>
                                      finishDraft(percentageDraftKey)
                                    }
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                      if (!canConfigureCurrency) return;
                                      const nextValue = sanitizeDecimalInput(
                                        e.target.value
                                      );
                                      changeDraft(
                                        percentageDraftKey,
                                        nextValue
                                      );
                                      setFees(
                                        upsertFeeRuleByType(
                                          fees,
                                          currency,
                                          row.type,
                                          {
                                            pricingStrategy:
                                              SubscriptionFeePricingStrategy.CUSTOM_FEES,
                                            percentageFee: parsePct(nextValue),
                                          }
                                        )
                                      );
                                    }}
                                  />
                                </Field>
                              )}
                            </>
                          )}
                          {fixedMonthlyPlanStrategy &&
                            isBulk &&
                            rule?.monthlyPlanKey == null && (
                              <>
                                <Field>
                                  Monthly fee ({billingSymbol})
                                  <TextInput
                                    type="text"
                                    inputMode="decimal"
                                    disabled={!canConfigureCurrency}
                                    placeholder={`${billingSymbol}0.00`}
                                    value={draftValue(
                                      fixedMonthlyFeeDraftKey,
                                      displayMajor(rule?.monthlyFeeMinor)
                                    )}
                                    onWheel={preventNumberInputWheel}
                                    onFocus={() =>
                                      startDraft(
                                        fixedMonthlyFeeDraftKey,
                                        displayMajor(rule?.monthlyFeeMinor)
                                      )
                                    }
                                    onBlur={() =>
                                      finishDraft(fixedMonthlyFeeDraftKey)
                                    }
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                      if (!canConfigureCurrency) return;
                                      const nextValue = sanitizeDecimalInput(
                                        e.target.value
                                      );
                                      changeDraft(
                                        fixedMonthlyFeeDraftKey,
                                        nextValue
                                      );
                                      setFees(
                                        upsertFeeRuleByType(
                                          fees,
                                          currency,
                                          row.type,
                                          {
                                            pricingStrategy:
                                              SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN,
                                            monthlyPlanKey: null,
                                            monthlyFeeMinor:
                                              parseMajorToPence(nextValue),
                                          }
                                        )
                                      );
                                    }}
                                  />
                                </Field>
                                <Field>
                                  Transaction limit
                                  <TextInput
                                    type="text"
                                    inputMode="numeric"
                                    disabled={!canConfigureCurrency}
                                    placeholder="0"
                                    value={draftValue(
                                      fixedMonthlyLimitDraftKey,
                                      rule?.monthlyUnitLimit != null
                                        ? String(rule.monthlyUnitLimit)
                                        : ""
                                    )}
                                    onWheel={preventNumberInputWheel}
                                    onFocus={() =>
                                      startDraft(
                                        fixedMonthlyLimitDraftKey,
                                        rule?.monthlyUnitLimit != null
                                          ? String(rule.monthlyUnitLimit)
                                          : ""
                                      )
                                    }
                                    onBlur={() =>
                                      finishDraft(fixedMonthlyLimitDraftKey)
                                    }
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                      if (!canConfigureCurrency) return;
                                      const nextValue = sanitizeIntegerInput(
                                        e.target.value
                                      );
                                      changeDraft(
                                        fixedMonthlyLimitDraftKey,
                                        nextValue
                                      );
                                      setFees(
                                        upsertFeeRuleByType(
                                          fees,
                                          currency,
                                          row.type,
                                          {
                                            pricingStrategy:
                                              SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN,
                                            monthlyPlanKey: null,
                                            monthlyUnitLimit:
                                              parseInteger(nextValue),
                                          }
                                        )
                                      );
                                    }}
                                  />
                                </Field>
                              </>
                            )}
                        </FieldsGrid>
                      </TableRow>
                    );
                  })}
                </Table>
              </>
            </ScheduleCard>
          );
        })}
    </Wrap>
  );
};

export default TransactionFeesScheduleEditor;
